/**
 * File: assets/basari-eye-test-store.js
 * Purpose: Eye Test Store Selection UI — mock StoreService + presentation.
 * Sprint: 1B.16B — Eye Test Store Selection UI
 * Owner: Basari Theme / PDP
 * Dependencies: basari-eye-test-booking.js (EyeTestController API), drawer host mount
 *
 * Rules:
 *   - Never write directly to lensFlow.eyeTestBooking
 *   - Store selection goes through EyeTestController.selectStore()
 *   - Navigation via EyeTestController.requestNavigation()
 *   - Close reuses drawer closeDrawer / onClose
 *   - No Google Maps / geolocation / backend / booking logic
 */
(function (global) {
  'use strict';

  var BasariEyeTestStore = global.BasariEyeTestStore || {};
  var READY_ATTR = 'data-basari-eye-test-store-ready';
  var SELECTED_CLASS = 'is-selected';
  var ROUTE_DATE_TIME = 'eye-test-date-time';

  var STATUS_OPEN = 'open';
  var STATUS_CLOSED = 'closed';

  /* ---------------------------------------------------------------------------
   * Temporary mock StoreService (client-side only — no fetch / AJAX / API)
   * ------------------------------------------------------------------------- */
  var MOCK_STORES = [
    {
      id: 'store-dubai-mall',
      name: 'Location Name – Near Dubai mall',
      address:
        'Ground Floor, Mall Of Emirates – Shop No D – 40A 1 – Al Barsha – Al Barsha 1 – Dubai – United Arab Emirates',
      status: STATUS_OPEN,
      closingTime: 'Closes at 10 pm',
      distance: '10.2 km',
      latitude: 25.1972,
      longitude: 55.2744
    },
    {
      id: 'store-marina-walk',
      name: 'Location Name – Near Dubai Marina',
      address:
        'Marina Walk – Shop No M – 12 – Dubai Marina – Dubai – United Arab Emirates',
      status: STATUS_OPEN,
      closingTime: 'Closes at 10 pm',
      distance: '12.8 km',
      latitude: 25.0805,
      longitude: 55.1403
    },
    {
      id: 'store-ibn-battuta',
      name: 'Location Name – Near Ibn Battuta Mall',
      address:
        'Ibn Battuta Mall – China Court – Shop No C – 08 – Jebel Ali – Dubai – United Arab Emirates',
      status: STATUS_OPEN,
      closingTime: 'Closes at 10 pm',
      distance: '18.4 km',
      latitude: 25.0444,
      longitude: 55.1175
    },
    {
      id: 'store-city-centre-deira',
      name: 'Location Name – Near City Centre Deira',
      address:
        'City Centre Deira – Ground Floor – Shop No G – 22 – Port Saeed – Deira – Dubai – United Arab Emirates',
      status: STATUS_OPEN,
      closingTime: 'Closes at 11 pm',
      distance: '8.1 km',
      latitude: 25.2512,
      longitude: 55.3331
    }
  ];

  function isObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  function cloneStore(store) {
    if (!isObject(store)) return null;
    return {
      id: store.id,
      name: store.name,
      address: store.address,
      status: store.status,
      closingTime: store.closingTime,
      distance: store.distance,
      latitude: store.latitude,
      longitude: store.longitude
    };
  }

  function StoreService(options) {
    options = options || {};
    this._stores = Array.isArray(options.stores) ? options.stores.slice() : MOCK_STORES.slice();
  }

  /** @returns {Array} Mock store list (no network) */
  StoreService.prototype.getStores = function () {
    var out = [];
    var i;
    for (i = 0; i < this._stores.length; i += 1) {
      out.push(cloneStore(this._stores[i]));
    }
    return out;
  };

  /** @returns {Object|null} */
  StoreService.prototype.getStoreById = function (id) {
    var key = String(id || '').trim();
    if (!key) return null;
    var i;
    for (i = 0; i < this._stores.length; i += 1) {
      if (String(this._stores[i].id) === key) {
        return cloneStore(this._stores[i]);
      }
    }
    return null;
  };

  /**
   * Client-side filter by store name / address only.
   * @param {string} query
   * @returns {Array}
   */
  StoreService.prototype.searchStores = function (query) {
    var q = String(query || '')
      .toLowerCase()
      .trim();
    var all = this.getStores();
    if (!q) return all;

    return all.filter(function (store) {
      var name = String((store && store.name) || '').toLowerCase();
      var address = String((store && store.address) || '').toLowerCase();
      return name.indexOf(q) !== -1 || address.indexOf(q) !== -1;
    });
  };

  function setHidden(el, hidden) {
    if (!el) return;
    if (hidden) {
      el.setAttribute('hidden', '');
    } else {
      el.removeAttribute('hidden');
    }
  }

  function statusLabel(status, labels) {
    labels = labels || {};
    if (String(status || '').toLowerCase() === STATUS_CLOSED) {
      return labels.closed || 'Closed';
    }
    return labels.open || 'Open Now';
  }

  /**
   * @param {HTMLElement} root - [data-basari-lens-eye-test-store-root]
   * @param {Object} options
   * @param {Function} options.getController - returns EyeTestController
   * @param {Function} [options.onClose]
   * @param {StoreService} [options.storeService]
   * @param {Object} [options.labels]
   */
  function EyeTestStoreUI(root, options) {
    this.root = root;
    this.options = options || {};
    this.storeService =
      this.options.storeService instanceof StoreService
        ? this.options.storeService
        : new StoreService();
    this.labels = this.options.labels || {};
    this.cache = {};
    this.bound = false;
    this.selectedStoreId = null;
    this._onRootEvent = this.onRootEvent.bind(this);
  }

  EyeTestStoreUI.prototype.getController = function () {
    if (typeof this.options.getController === 'function') {
      return this.options.getController();
    }
    return null;
  };

  EyeTestStoreUI.prototype.cacheSelectors = function () {
    this.cache.search = this.root.querySelector('[data-basari-eye-test-store-search]');
    this.cache.list = this.root.querySelector('[data-basari-eye-test-store-list]');
    this.cache.empty = this.root.querySelector('[data-basari-eye-test-store-empty]');
    this.cache.template = this.root.querySelector('[data-basari-eye-test-store-card-template]');
    this.cache.confirmBtn = this.root.querySelector('[data-basari-eye-test-store-confirm]');
    this.cache.closeBtn = this.root.querySelector('[data-basari-eye-test-store-close]');
    this.cache.a11y = this.root.querySelector('[data-basari-lens-eye-test-a11y]');
  };

  EyeTestStoreUI.prototype.bind = function () {
    if (this.bound || !this.root) return;
    this.root.addEventListener('click', this._onRootEvent);
    this.root.addEventListener('keydown', this._onRootEvent);
    this.root.addEventListener('input', this._onRootEvent);
    this.bound = true;
  };

  EyeTestStoreUI.prototype.unbind = function () {
    if (!this.bound || !this.root) return;
    this.root.removeEventListener('click', this._onRootEvent);
    this.root.removeEventListener('keydown', this._onRootEvent);
    this.root.removeEventListener('input', this._onRootEvent);
    this.bound = false;
  };

  EyeTestStoreUI.prototype.onRootEvent = function (event) {
    var target = event.target;
    if (!target || !this.root.contains(target)) return;

    if (event.type === 'input') {
      if (target.matches && target.matches('[data-basari-eye-test-store-search]')) {
        this.renderList(target.value);
      }
      return;
    }

    var card = target.closest ? target.closest('[data-basari-eye-test-store-item]') : null;

    if (event.type === 'keydown' && card) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.selectStoreById(card.getAttribute('data-store-id'));
      }
      return;
    }

    if (event.type === 'click') {
      if (card) {
        this.selectStoreById(card.getAttribute('data-store-id'));
        return;
      }

      if (target.closest && target.closest('[data-basari-eye-test-store-confirm]')) {
        this.handleConfirm();
        return;
      }

      if (target.closest && target.closest('[data-basari-eye-test-store-close]')) {
        this.handleClose();
      }
    }
  };

  EyeTestStoreUI.prototype.readSelectedFromState = function () {
    var controller = this.getController();
    if (!controller || typeof controller.getState !== 'function') return null;
    var state = controller.getState();
    if (!state || !state.selectedStore) return null;
    if (typeof state.selectedStore === 'string') return state.selectedStore;
    if (isObject(state.selectedStore) && state.selectedStore.id != null) {
      return String(state.selectedStore.id);
    }
    return null;
  };

  EyeTestStoreUI.prototype.selectStoreById = function (id) {
    var store = this.storeService.getStoreById(id);
    if (!store) return;

    this.selectedStoreId = store.id;

    var controller = this.getController();
    if (controller && typeof controller.selectStore === 'function') {
      controller.selectStore(store);
    }

    this.syncSelectionUI();
    this.syncConfirmEnabled();
    this.announceSelection(store);
  };

  EyeTestStoreUI.prototype.syncSelectionUI = function () {
    var items = this.cache.list
      ? Array.prototype.slice.call(
          this.cache.list.querySelectorAll('[data-basari-eye-test-store-item]')
        )
      : [];
    var selectedId = String(this.selectedStoreId || '');
    var i;
    var item;
    var card;
    var isSelected;

    for (i = 0; i < items.length; i += 1) {
      item = items[i];
      card = item.querySelector('[data-basari-eye-test-store-card]');
      isSelected = String(item.getAttribute('data-store-id') || '') === selectedId;
      if (isSelected) {
        item.setAttribute('aria-selected', 'true');
        if (card) card.classList.add(SELECTED_CLASS);
      } else {
        item.setAttribute('aria-selected', 'false');
        if (card) card.classList.remove(SELECTED_CLASS);
      }
    }
  };

  EyeTestStoreUI.prototype.syncConfirmEnabled = function () {
    var btn = this.cache.confirmBtn;
    if (!btn) return;
    var enabled = !!this.selectedStoreId;
    btn.disabled = !enabled;
    btn.setAttribute('aria-disabled', enabled ? 'false' : 'true');
  };

  EyeTestStoreUI.prototype.announceSelection = function (store) {
    if (!this.cache.a11y || !store) return;
    var name = String(store.name || '').trim();
    if (!name) return;
    this.cache.a11y.textContent = name + ' selected.';
  };

  EyeTestStoreUI.prototype.fillCard = function (item, store) {
    if (!item || !store) return;

    var card = item.querySelector
      ? item.querySelector('[data-basari-eye-test-store-card]')
      : null;
    if (!card) {
      card = item.matches && item.matches('[data-basari-eye-test-store-card]') ? item : null;
    }
    if (!card) return;

    var host = item.matches && item.matches('[data-basari-eye-test-store-item]') ? item : card;

    host.setAttribute('data-store-id', String(store.id || ''));
    host.setAttribute('aria-selected', 'false');
    card.classList.remove(SELECTED_CLASS);

    var nameEl = card.querySelector('[data-basari-eye-test-store-name]');
    var addressEl = card.querySelector('[data-basari-eye-test-store-address]');
    var statusEl = card.querySelector('[data-basari-eye-test-store-status]');
    var closingEl = card.querySelector('[data-basari-eye-test-store-closing]');
    var distanceEl = card.querySelector('[data-basari-eye-test-store-distance]');
    var statusDot = card.querySelector('[data-basari-eye-test-store-status-dot]');

    if (nameEl) nameEl.textContent = store.name || '';
    if (addressEl) addressEl.textContent = store.address || '';
    if (statusEl) {
      statusEl.textContent = statusLabel(store.status, {
        open: this.labels.openNow,
        closed: this.labels.closed
      });
    }
    if (closingEl) closingEl.textContent = store.closingTime || '';
    if (distanceEl) distanceEl.textContent = store.distance || '';

    if (statusDot) {
      var isOpen = String(store.status || '').toLowerCase() !== STATUS_CLOSED;
      statusDot.classList.toggle('is-open', isOpen);
      statusDot.classList.toggle('is-closed', !isOpen);
    }
  };

  EyeTestStoreUI.prototype.createCardItem = function (store) {
    var template = this.cache.template;
    if (!template || !template.content) return null;

    var fragment = template.content.cloneNode(true);
    var item = fragment.querySelector('[data-basari-eye-test-store-item]');
    if (!item) return null;

    this.fillCard(item, store);
    return item;
  };

  EyeTestStoreUI.prototype.renderList = function (query) {
    if (!this.cache.list) return;

    var stores =
      query != null && String(query).length
        ? this.storeService.searchStores(query)
        : this.storeService.getStores();

    this.cache.list.innerHTML = '';

    var i;
    var item;
    for (i = 0; i < stores.length; i += 1) {
      item = this.createCardItem(stores[i]);
      if (item) this.cache.list.appendChild(item);
    }

    setHidden(this.cache.empty, stores.length > 0);
    this.syncSelectionUI();
    this.syncConfirmEnabled();
  };

  EyeTestStoreUI.prototype.handleConfirm = function () {
    if (!this.selectedStoreId) return;

    var controller = this.getController();
    if (controller && typeof controller.requestNavigation === 'function') {
      controller.requestNavigation(ROUTE_DATE_TIME);
      return;
    }

    if (
      global.BasariEyeTestBooking &&
      global.BasariEyeTestBooking.ROUTES &&
      typeof this.options.navigate === 'function'
    ) {
      this.options.navigate(global.BasariEyeTestBooking.ROUTES.DATE_TIME);
    }
  };

  EyeTestStoreUI.prototype.handleClose = function () {
    if (typeof this.options.onClose === 'function') {
      this.options.onClose();
      return;
    }

    var controller = this.getController();
    if (controller && typeof controller.closeDrawer === 'function') {
      controller.closeDrawer();
    }
  };

  EyeTestStoreUI.prototype.refresh = function () {
    this.selectedStoreId = this.readSelectedFromState();
    var query = this.cache.search ? this.cache.search.value : '';
    this.renderList(query);
  };

  EyeTestStoreUI.prototype.destroy = function () {
    this.unbind();
    if (this.root) {
      this.root.removeAttribute(READY_ATTR);
    }
    this.cache = {};
    this.selectedStoreId = null;
  };

  /**
   * @param {HTMLElement} root
   * @param {Object} options
   * @returns {EyeTestStoreUI|null}
   */
  function mount(root, options) {
    if (!root) return null;

    if (root.getAttribute(READY_ATTR) === 'true' && root._basariEyeTestStoreUI) {
      root._basariEyeTestStoreUI.refresh();
      return root._basariEyeTestStoreUI;
    }

    var ui = new EyeTestStoreUI(root, options || {});
    ui.cacheSelectors();
    ui.bind();
    ui.refresh();
    root.setAttribute(READY_ATTR, 'true');
    root._basariEyeTestStoreUI = ui;
    return ui;
  }

  BasariEyeTestStore.StoreService = StoreService;
  BasariEyeTestStore.MOCK_STORES = MOCK_STORES;
  BasariEyeTestStore.mount = mount;
  BasariEyeTestStore.EyeTestStoreUI = EyeTestStoreUI;

  global.BasariEyeTestStore = BasariEyeTestStore;
})(typeof window !== 'undefined' ? window : this);
